import type { HttpInterceptorFn } from '@angular/common/http';
import { HttpContextToken, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { ToastBusService } from '../error/toast-bus.service';
import { TRACE_ID } from './trace-id.interceptor';

/**
 * Opt-out del toast global: un request lo setea en `true` cuando su fallo YA se
 * muestra en contexto (p. ej. un `pvp-error-retry` inline). Sin esto, una carga
 * fallida apila el aviso inline + este toast + el toast del store = 3 avisos por
 * una sola falla (LION-722). El interceptor sigue LOGUEANDO la correlación: sólo
 * calla el toast redundante, no la observabilidad. Los flujos fire-and-forget /
 * en background NO lo setean, así que su error sigue toasteando (no silenciar).
 */
export const SUPPRESS_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * Traduce errores HTTP a toasts para el operador y re-lanza para que el caller
 * decida (los stores/facades pueden manejar casos específicos).
 *
 * - 401 no se toastea acá: el flujo OIDC lo maneja (renew / re-login).
 * - 403 → aviso de permiso (la UI ya debería ocultarlo; esto es red-team).
 * - 5xx / 0 → error de servidor/red con trace-id para soporte.
 * - Requests con `SUPPRESS_ERROR_TOAST` → sin toast (el error se muestra inline).
 */
export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const toasts = inject(ToastBusService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        const traceId = req.context.get(TRACE_ID) || undefined;
        logCorrelation(error, traceId);
        if (!req.context.get(SUPPRESS_ERROR_TOAST)) {
          notify(toasts, error, traceId);
        }
      }
      return throwError(() => error);
    }),
  );
};

/**
 * Loguea el `x-request-id` CONFIRMADO por el backend en la respuesta de error para
 * correlacionar con sus logs. Sólo en fallos de servidor/red (5xx / 0): los 4xx son
 * esperados y no queremos ruido. Normalmente coincide con el `X-Request-Id` que
 * mandó el traceIdInterceptor; si difiere, el backend generó el suyo.
 */
function logCorrelation(error: HttpErrorResponse, traceId?: string): void {
  if (error.status >= 400 && error.status < 500) return;
  const requestId = sanitizeForLog(error.headers.get('x-request-id'));
  // eslint-disable-next-line no-console
  console.error('[http] request fallida', {
    status: error.status,
    url: sanitizeForLog(error.url),
    traceId,
    requestId,
  });
}

/**
 * Neutraliza valores controlados por la respuesta (URL, headers) antes de
 * loguearlos: los caracteres de control (CR/LF incluidos) permitirían forjar
 * líneas de log, y el largo se acota para no inflar la salida.
 */
function sanitizeForLog(value: string | null | undefined): string | undefined {
  if (value == null) return undefined;
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\u0000-\u001f\u007f]+/g, ' ').slice(0, 300);
}

function notify(
  toasts: ToastBusService,
  error: HttpErrorResponse,
  traceId?: string,
): void {
  switch (true) {
    case error.status === 401:
      // Manejado por el stack OIDC — no molestar al operador.
      return;
    case error.status === 403:
      toasts.error('Acceso denegado', 'No tenés permisos para esta acción.', traceId);
      return;
    case error.status === 404:
      toasts.warning('No encontrado', 'El recurso solicitado no existe.');
      return;
    case error.status === 409:
      toasts.warning('Conflicto', serverMessage(error) ?? 'El recurso cambió, reintentá.');
      return;
    case error.status === 422:
      toasts.warning('Datos inválidos', serverMessage(error) ?? 'Revisá los campos.');
      return;
    case error.status === 0:
      toasts.error(
        'Sin conexión',
        'No se pudo contactar al servidor. Reintentando…',
        traceId,
      );
      return;
    case error.status >= 500:
      toasts.error(
        'Error del servidor',
        serverMessage(error) ?? `Error ${error.status}. Referencia: ${traceId ?? 's/d'}`,
        traceId,
      );
      return;
    default:
      toasts.error('Error', serverMessage(error) ?? `HTTP ${error.status}`, traceId);
  }
}

function serverMessage(error: HttpErrorResponse): string | undefined {
  const body = error.error as { message?: string | string[] } | string | null;
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object' && body.message) {
    return Array.isArray(body.message) ? body.message.join(' · ') : body.message;
  }
  return undefined;
}
